import { z } from 'zod';
import { AddressSchema, AuditSchema, DateOnlySchema, IdSchema } from './common.ts';

export const JOB_STATUSES = ['lead', 'open', 'closed', 'archived'] as const;
export const JobStatusSchema = z.enum(JOB_STATUSES);
export type JobStatus = z.infer<typeof JobStatusSchema>;

/** Which modules the owner can see for this job, on top of company defaults. */
export const PortalModulesSchema = z.object({
  schedule: z.boolean(),
  dailyLogs: z.boolean(),
  todos: z.boolean(),
  changeOrders: z.boolean(),
  selections: z.boolean(),
  files: z.boolean(),
  messaging: z.boolean(),
  invoices: z.boolean(),
  budget: z.boolean(),
});
export type PortalModules = z.infer<typeof PortalModulesSchema>;

export const DEFAULT_PORTAL_MODULES: PortalModules = {
  schedule: true,
  dailyLogs: true,
  todos: true,
  changeOrders: true,
  selections: true,
  files: true,
  messaging: true,
  invoices: true,
  // Off by default: the budget exposes commercial detail that most
  // fixed-price jobs should not share.
  budget: false,
};

/**
 * Whether the owner's portal shows one module for this job.
 *
 * One function because there are two callers asking one question — the
 * portal's navigation, which decides whether to offer a page, and the page
 * itself, which decides what to draw. Two copies of
 * `job.portalModules?.budget === true` is how a link comes to lead
 * somewhere that says you cannot be here.
 *
 * **An absent flag falls back to the default, not to `false`.** The field
 * is required by the schema, so this only arises for a job written before
 * a module existed — and such a job should behave like a new one:
 * `schedule` stays on, `budget` stays off. Defaulting to `false` would
 * silently hide the schedule from every owner on an older job, which is a
 * worse failure than showing a budget page that explains itself.
 *
 * This is **not** a confidentiality boundary. The security rules do not
 * consult it, and deliberately: an owner who has been shown a price has
 * been shown it, so this is about what the builder chose to put on the
 * portal. Where a module carries something the owner must never see, the
 * answer is a derived document with the secret left out —
 * `ownerChangeOrders`, `ownerBudgetLines` — not a flag on the job.
 */
export function portalShows(
  job: { readonly portalModules?: Partial<PortalModules> } | null | undefined,
  module: keyof PortalModules,
): boolean {
  if (!job) return false;
  return job.portalModules?.[module] ?? DEFAULT_PORTAL_MODULES[module];
}

export const JobSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  name: z.string().min(1),
  /** Prefix for per-job document numbering, e.g. "GLN" giving GLN-0001. */
  prefix: z.string().min(1).max(8),
  status: JobStatusSchema,
  address: AddressSchema,
  timeZone: z.string(),
  ownerContactIds: z.array(IdSchema).default([]),
  projectManagerUids: z.array(IdSchema).default([]),
  internalUids: z.array(IdSchema).default([]),
  subContactIds: z.array(IdSchema).default([]),
  startDate: DateOnlySchema.optional(),
  projectedEndDate: DateOnlySchema.optional(),
  actualEndDate: DateOnlySchema.optional(),
  contractType: z.enum(['fixed', 'openBook']).default('fixed'),
  colour: z.string().optional(),
  tags: z.array(z.string()).default([]),
  scheduleOnline: z.boolean().default(false),
  portalModules: PortalModulesSchema.default(DEFAULT_PORTAL_MODULES),
});
export type Job = z.infer<typeof JobSchema>;
